import type { TimelineEntry, UserMessageEntry } from '../types/types';

/** Max line width in pixels */
export const MAX_LINE_LENGTH = 13;

/** Consistent line weight */
export const LINE_HEIGHT = 2;

/**
 * Converts markdown to plain text for preview display.
 * Strips tables, formatting, and other markdown syntax.
 * @internal Exported for testing
 */
export const markdownToPlainText = (markdown: string): string => {
  let text = markdown;

  // Strip XML/HTML-style tags (e.g. <usernotes>, <keypoints>, <cite-badge>)
  // Bounded quantifiers {0,500} prevent ReDoS (SonarQube S5852)
  text = text.replaceAll(/<\/?[a-z][a-z0-9-]{0,50}(?:\s[^>]{0,500})?\/?>/gi, '');

  // Remove table rows (lines that start/end with | or contain |---|)
  text = text
    .split('\n')
    .filter((line) => {
      const trimmedLine = line.trim();
      // Skip table separator rows (|---|---|)
      if (/^\|?[\s-:|]+\|?$/.test(trimmedLine)) return false;
      // Skip empty lines
      if (!trimmedLine) return false;
      return true;
    })
    .map((line) => {
      // Convert table rows to plain text (extract cell contents)
      if (line.includes('|')) {
        return line
          .split('|')
          .map((cell) => cell.trim())
          .filter((cell) => cell.length > 0)
          .join(' – ');
      }
      return line;
    })
    .join(' ');

  // Remove markdown formatting
  text = text
    // Remove headers (# ## ### etc)
    .replace(/^#{1,6}\s+/gm, '')
    // Remove inline code (`code`)
    .replace(/`([^`]+)`/g, '$1')
    // Remove links [text](url) -> text
    // Use bounded quantifiers {1,500} to prevent ReDoS attacks (SonarQube S5852)
    .replace(/\[([^\]]{1,500})\]\([^)]{1,2000}\)/g, '$1')
    // Remove horizontal rules (---, ***, ___) - match 3+ consecutive chars
    .replace(/(?:^|\s)([-]{3,}|[*]{3,}|[_]{3,})(?:\s|$)/g, ' ');

  // Remove bold/italic - run multiple times to handle nested cases like **_text_**
  // Process bold first (** or __), then italic (* or _)
  let prevText = '';
  while (prevText !== text) {
    prevText = text;
    text = text.replace(/(\*\*|__)(.*?)\1/g, '$2').replace(/(\*|_)([^*_]+)\1/g, '$2');
  }

  // Collapse multiple spaces/newlines
  text = text.replace(/\s+/g, ' ');

  return text.trim();
};

/**
 * Truncates message for tooltip preview.
 * Converts markdown to plain text first.
 */
export const truncateForPreview = (message: string, maxLength: number = 80): string => {
  const plainText = markdownToPlainText(message);
  if (plainText.length <= maxLength) return plainText;
  const truncated = plainText.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > maxLength * 0.7) {
    return `${truncated.slice(0, lastSpace)}...`;
  }
  return `${truncated}...`;
};

/**
 * Extracts user message entries from the timeline.
 */
export const extractUserMessages = (timeline: TimelineEntry[]): UserMessageEntry[] => {
  return timeline.filter(
    (entry): entry is UserMessageEntry => entry.type === 'user_message'
  );
};

